
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Target, DollarSign, Activity, Calendar, Download, FileText } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';
import { ROUTES } from '../constants';

// Historical Data (Source: CTC Algo Engine 2.0 Reports Sept-Dec 2025)
const allData = {
    totalPips: 28915,
    winRate: 62.5,
    profitFactor: 2.45,
    totalTrades: 410,

    equityData: [
        { month: 'Set', equity: 4070 },
        { month: 'Ott', equity: 21840 },
        { month: 'Nov', equity: 22996 },
        { month: 'Dic', equity: 28915 },
    ],

    monthlyPerformance: [
        { month: 'Settembre 2025', pips: 4070, trades: 71, winRate: 52.1 },
        { month: 'Ottobre 2025', pips: 17770, trades: 143, winRate: 66.0 },
        { month: 'Novembre 2025', pips: 1156, trades: 56, winRate: 66.1 },
        { month: 'Dicembre 2025', pips: 5917, trades: 140, winRate: 62.0 },
    ],

    assetDistribution: [
        { name: 'Forex', value: 85, color: '#3b82f6' },
        { name: 'Indices', value: 10, color: '#f59e0b' },
        { name: 'Crypto', value: 5, color: '#10b981' },
    ],
};

// Data for 3 months (Oct, Nov, Dec)
const threeMonthsData = {
    totalPips: 24843,
    winRate: 64.8,
    profitFactor: 2.52,
    totalTrades: 339,

    equityData: [
        { month: 'Ott', equity: 17770 },
        { month: 'Nov', equity: 22996 },
        { month: 'Dic', equity: 28915 },
    ],

    monthlyPerformance: [
        { month: 'Ottobre 2025', pips: 17770, trades: 143, winRate: 66.0 },
        { month: 'Novembre 2025', pips: 1156, trades: 56, winRate: 66.1 },
        { month: 'Dicembre 2025', pips: 5917, trades: 140, winRate: 62.0 },
    ],

    assetDistribution: [
        { name: 'Forex', value: 85, color: '#3b82f6' },
        { name: 'Indices', value: 10, color: '#f59e0b' },
        { name: 'Crypto', value: 5, color: '#10b981' },
    ],
};

// Data for 1 month (Dec only)
const oneMonthData = {
    totalPips: 5917,
    winRate: 62.0,
    profitFactor: 2.15,
    totalTrades: 140,

    equityData: [
        { month: 'Dic', equity: 28915 },
    ],

    monthlyPerformance: [
        { month: 'Dicembre 2025', pips: 5917, trades: 140, winRate: 62.0 },
    ],

    assetDistribution: [
        { name: 'Forex', value: 85, color: '#3b82f6' },
        { name: 'Indices', value: 10, color: '#f59e0b' },
        { name: 'Crypto', value: 5, color: '#10b981' },
    ],
};

// Monthly Reports
const monthlyReports = [
    { month: 'Settembre 2025', file: '/documents/algo-engine/01_ CTC ALGO ENGINE - Settembre 2025.pdf', color: 'blue' },
    { month: 'Ottobre 2025', file: '/documents/algo-engine/02_ CTC ALGO ENGINE - Ottobre 2025.pdf', color: 'green' },
    { month: 'Novembre 2025', file: '/documents/algo-engine/03_ CTC ALGO ENGINE - Novembre 2025.pdf', color: 'orange' },
    { month: 'Dicembre 2025', file: '/documents/algo-engine/04_ CTC ALGO ENGINE - Dicembre 2025.pdf', color: 'purple' },
];

export const AlgoEngineDashboardPage: React.FC = () => {
    const { t } = useLanguage();
    const [selectedPeriod, setSelectedPeriod] = useState('all');

    // Select data based on period
    const mockData = selectedPeriod === '1m' ? oneMonthData : selectedPeriod === '3m' ? threeMonthsData : allData;

    const handleDownload = (file: string, month: string) => {
        const link = document.createElement('a');
        link.href = file;
        link.download = `CTC_ALGO_ENGINE_${month.replace(' ', '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#050508] via-[#0A0E1A] to-[#050508] text-white">
            {/* Header */}
            <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <Link
                        to={ROUTES.FINANCE}
                        className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8 group"
                    >
                        <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={20} />
                        Torna a Finanza
                    </Link>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                        <div>
                            <h1 className="text-4xl sm:text-5xl font-black mb-4">
                                <span className="text-white">ALGO ENGINE</span>
                                <span className="text-orange-500 ml-3">DASHBOARD</span>
                            </h1>
                            <p className="text-gray-400 text-lg">Monitoraggio Real-Time Performance Algoritmica</p>
                        </div>

                        {/* Period Filter */}
                        <div className="flex gap-2 mt-6 md:mt-0">
                            {['all', '3m', '1m'].map((period) => (
                                <button
                                    key={period}
                                    onClick={() => setSelectedPeriod(period)}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${selectedPeriod === period
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                        }`}
                                >
                                    {period === 'all' ? 'Tutto' : period === '3m' ? '3 Mesi' : '1 Mese'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Free Trial Button */}
                    <div className="mb-8">
                        <FreeTrialButton serviceName="Algo Engine 2.0" />
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        <AnimatedSection delay={100}>
                            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-2xl p-6 hover:border-blue-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <TrendingUp className="text-blue-500" size={32} />
                                    <span className="text-xs text-blue-400 font-semibold px-3 py-1 bg-blue-500/20 rounded-full">
                                        TOTALE
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    +{mockData.totalPips.toLocaleString()}
                                </h3>
                                <p className="text-gray-400 text-sm">Pips Totali</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={200}>
                            <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-2xl p-6 hover:border-green-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <Target className="text-green-500" size={32} />
                                    <span className="text-xs text-green-400 font-semibold px-3 py-1 bg-green-500/20 rounded-full">
                                        ACCURACY
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    {mockData.winRate}%
                                </h3>
                                <p className="text-gray-400 text-sm">Win Rate</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={300}>
                            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-2xl p-6 hover:border-orange-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <DollarSign className="text-orange-500" size={32} />
                                    <span className="text-xs text-orange-400 font-semibold px-3 py-1 bg-orange-500/20 rounded-full">
                                        RATIO
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    {mockData.profitFactor}
                                </h3>
                                <p className="text-gray-400 text-sm">Profit Factor</p>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection delay={400}>
                            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-2xl p-6 hover:border-purple-500/40 transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <Activity className="text-purple-500" size={32} />
                                    <span className="text-xs text-purple-400 font-semibold px-3 py-1 bg-purple-500/20 rounded-full">
                                        OPERAZIONI
                                    </span>
                                </div>
                                <h3 className="text-3xl font-black text-white mb-1">
                                    {mockData.totalTrades}
                                </h3>
                                <p className="text-gray-400 text-sm">Trade Totali</p>
                            </div>
                        </AnimatedSection>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
                        {/* Equity Chart */}
                        <AnimatedSection delay={500} className="lg:col-span-2">
                            <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                                <h3 className="text-xl font-bold mb-6 flex items-center">
                                    <TrendingUp className="mr-2 text-orange-500" size={24} />
                                    Crescita Equity (Pips)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={mockData.equityData}>
                                        <defs>
                                            <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                                        <XAxis dataKey="month" stroke="#6b7280" />
                                        <YAxis stroke="#6b7280" />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                                            labelStyle={{ color: '#f3f4f6' }}
                                        />
                                        <Area type="monotone" dataKey="equity" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorEquity)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </AnimatedSection>

                        {/* Asset Distribution */}
                        <AnimatedSection delay={600}>
                            <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                                <h3 className="text-xl font-bold mb-6 flex items-center">
                                    <Activity className="mr-2 text-blue-500" size={24} />
                                    Distribuzione Asset
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={mockData.assetDistribution}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {mockData.assetDistribution.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </AnimatedSection>
                    </div>

                    {/* Monthly Performance */}
                    <AnimatedSection delay={700}>
                        <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6 mb-12">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <Calendar className="mr-2 text-green-500" size={24} />
                                Performance Mensile
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {mockData.monthlyPerformance.map((month, index) => (
                                    <div key={index} className="bg-[#1a1f2e] border border-gray-700 rounded-xl p-4">
                                        <h4 className="text-sm font-semibold text-gray-400 mb-3">{month.month}</h4>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs text-gray-500">Pips:</span>
                                                <span className="text-lg font-bold text-green-400">+{month.pips.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs text-gray-500">Trade:</span>
                                                <span className="text-sm font-semibold text-white">{month.trades}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs text-gray-500">Win Rate:</span>
                                                <span className="text-sm font-semibold text-blue-400">{month.winRate}%</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </AnimatedSection>

                    {/* Monthly Reports Download Section */}
                    <AnimatedSection delay={800}>
                        <div className="bg-[#0F1419] border border-gray-800 rounded-2xl p-6">
                            <h3 className="text-xl font-bold mb-6 flex items-center">
                                <FileText className="mr-2 text-orange-500" size={24} />
                                Resoconti Mensili
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {monthlyReports.map((report, index) => (
                                    <div
                                        key={index}
                                        className={`bg-gradient-to-br from-${report.color}-500/10 to-${report.color}-600/5 border border-${report.color}-500/20 rounded-xl p-5 hover:border-${report.color}-500/40 transition-all group`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <FileText className={`text-${report.color}-500`} size={28} />
                                            <Calendar className={`text-${report.color}-400`} size={20} />
                                        </div>
                                        <h4 className="text-sm font-bold text-white mb-4">{report.month}</h4>
                                        <button
                                            onClick={() => handleDownload(report.file, report.month)}
                                            className={`w-full bg-${report.color}-600 hover:bg-${report.color}-500 text-white font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 group-hover:scale-105`}
                                        >
                                            <Download size={16} />
                                            Scarica PDF
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
};
